import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import type { PrimitiveDef, PrimitiveValue } from "../../types"

interface Props {
  value: PrimitiveValue | undefined
  prim: PrimitiveDef
  /** Override displayed text (e.g. resolve a GroupNode UUID to its name) */
  displayOverride?: string | null
  /** Error message for computed formula cells */
  formulaError?: string | null
}

function shortSource(source: string): string {
  const name = source.replace(/\.(xlsx|csv|xls|tsv)$/i, "")
  return name.length > 18 ? name.slice(0, 16) + "…" : name
}

export function PivotCell({
  value,
  prim,
  displayOverride,
  formulaError,
}: Props) {
  const displayValue = displayOverride ?? value?.value
  const { translate } = useTranslation()

  // Formula cells
  if (prim.type === "formula") {
    return (
      <div
        className={clsx(
          "flex min-h-[24px] items-center gap-1 whitespace-nowrap rounded px-1",
        )}
      >
        {formulaError ? (
          <span className="text-sm italic text-warning" title={formulaError}>
            —
          </span>
        ) : displayValue ? (
          <span className="font-mono text-sm text-grey6">{displayValue}</span>
        ) : (
          <span className="text-sm italic text-grey5">—</span>
        )}
        {formulaError && (
          <span className="text-xs text-warning" title={formulaError}>
            ⚠
          </span>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-0.5">
      {value?.source && value.source !== "manual" && (
        <span className="text-[10px] leading-tight text-grey5/70">
          {translate("QMS/PIVOT/VIA", { source: shortSource(value.source) })}
        </span>
      )}
      <div
        className={clsx(
          "flex min-h-[24px] items-center gap-1 whitespace-nowrap rounded px-1",
        )}
      >
        {displayValue ? (
          <span
            className={clsx(
              "text-sm",
              value?.needs_cleaning &&
                "text-warning underline decoration-dashed",
              prim.is_pk && "font-mono font-medium",
            )}
          >
            {displayValue}
          </span>
        ) : (
          <span className="text-sm italic text-grey5">—</span>
        )}
        {value?.source &&
          value.source !== "manual" &&
          value.source !== "seed" && (
            <span
              className="rounded bg-success/20 px-1 text-[10px] text-success opacity-0 group-hover/cell:opacity-100"
              title={translate("QMS/PIVOT/SOURCE_TITLE", { source: value.source })}
            >
              {value.source}
            </span>
          )}
        {value?.needs_cleaning && (
          <span
            className="text-xs text-warning"
            title={translate("QMS/PIVOT/NEEDS_CLEANUP")}
          >
            ⚠
          </span>
        )}
      </div>
    </div>
  )
}
